import type { ConversationDeleteMessageDatagramBuf } from "~/classes/generated/message"
import type { ConversationDeleteResponseDatagramBuf } from "~/classes/generated/response"

import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { DatagramBuf } from "~/classes/generated/datagram"
import { ConversationDeleteRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addConversationToBuf, parseConversation } from "~/utils/flatbuffers/conversation"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { parseConversationId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("conversation/delete")

export function sendConversationDeleteRequests(requests: ConversationDeleteRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const deleteRequests = []
  for (const request of requests) {
    const requestId = addRequestIdToBuf(builder, request.requestId)
    const conversation = addConversationToBuf(builder, request.conversation)

    ConversationDeleteRequestDatagramBuf.startConversationDeleteRequestDatagramBuf(builder)
    ConversationDeleteRequestDatagramBuf.addRequestId(builder, requestId)
    ConversationDeleteRequestDatagramBuf.addOffline(builder, request.offline)
    ConversationDeleteRequestDatagramBuf.addConversation(builder, conversation)

    const deleteRequest = ConversationDeleteRequestDatagramBuf.endConversationDeleteRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addConversationDeleteRequest(builder, deleteRequest)
    deleteRequests.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const del = DatagramBuf.createRequestVector(builder, deleteRequests)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, del)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleConversationDeleteResponseDatagramBuf(datagram: ConversationDeleteResponseDatagramBuf) {
  onConversationDeleteResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    conversation: maybe(parseConversation)(datagram.conversation()),
    offline: datagram.offline(),
    conversationId: parseConversationId(datagram.conversationId()!),
  })
}

export function onConversationDeleteResponse(response: ConversationDeleteResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received conversation delete", response)

  emitInterfaceEvent("conversationDeleteResponse", response, response.requestId)

  if (response.error)
    return onConversationDeleteErrored(response.error)
  if (!response.conversation)
    throw new Error("No conversation in delete response")

  const iConversation = new StaticConversation(response.conversation)
  emitEntityEvent("conversationProperties", iConversation.id, iConversation.inner(), response.offline)
}

function onConversationDeleteErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  return notifyDatagramError(error, "Conversation delete request was unsuccessful")
}

export function handleConversationDeleteMessageDatagramBuf(datagram: ConversationDeleteMessageDatagramBuf) {
  onConversationDeleteMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    conversation: parseConversation(datagram.conversation()!),
  })
}

export function onConversationDeleteMessage(message: ConversationDeleteMessageDatagram) {
  const { removeConversation } = useConversations()
  const { onCreateOrOpenLatestConversationIntent } = useAgent()

  console.debug("Received conversation delete message", clone(message))

  // TODO: create a conversation deleted entity event so we don't have to call this
  // manually here.

  removeConversation(message.conversation.id)
  onCreateOrOpenLatestConversationIntent()
}
